import React from 'react';

// Material-UI
import {
    styled,
    Box,
    Hidden,
    Typography,
    Button,
    Paper,
    Step,
    Stepper as _Stepper,
    StepLabel as _StepLabel,
    CircularProgress,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

// Components
import OrderSummary from 'components/OrderSummary';
import CustomerForm from 'components/CustomerForm';
import PaymentForm from 'components/PaymentForm';
import Registration from 'registration/Registration';

// Utility
import { Trans } from '@lingui/macro';
import { useCheckoutContext } from 'context/CheckoutContext';

export default function Checkout() {
    const {
        event,
        paidTransaction,
        status,
        setStatus,
        contentRef,
        registrationEnabled,
        registrationForm,
    } = useCheckoutContext();

    return (
        <Box flex={1}>
            <Box ref={contentRef} />

            {status === 'Loading' && (
                <Container>
                    <CircularProgress />
                </Container>
            )}

            {status === 'Error' && (
                <Container>
                    <Typography>
                        <Trans>
                            Unable to proceed with checkout. Please close this window and try again.
                        </Trans>
                    </Typography>
                </Container>
            )}

            {['Registration', 'Checkout', 'Submit'].includes(status) &&
                registrationEnabled === true && (
                    <Header>
                        {status === 'Checkout' && (
                            <Box width="80px">
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => setStatus('Registration')}
                                >
                                    Back
                                </Button>
                            </Box>
                        )}
                        <Box flexGrow={1}>
                            <Stepper
                                activeStep={status === 'Registration' ? 0 : 1}
                                alternativeLabel
                            >
                                <Step key={1}>
                                    <StepLabel>
                                        <Trans>Form</Trans>
                                    </StepLabel>
                                </Step>
                                <Step key={2}>
                                    <StepLabel>
                                        <Trans>Payment</Trans>
                                    </StepLabel>
                                </Step>
                            </Stepper>
                        </Box>
                    </Header>
                )}

            {status === 'Registration' && registrationForm !== null && <Registration />}

            {(status === 'Checkout' || status === 'Submit') && (
                <Box pb={'270px'}>
                    <Root>
                        <Box width={[1, 1, 60 / 100]} p={2}>
                            <CustomerForm />
                            {paidTransaction && <PaymentForm />}
                            <Hidden mdUp>
                                <OrderSummary />
                            </Hidden>
                        </Box>
                        <Hidden smDown>
                            <Box width={40 / 100} p={2}>
                                <OrderSummary />
                            </Box>
                        </Hidden>
                    </Root>
                    <Root>
                        <Box my={4} mx={2}>
                            <FinePrint>{event.finePrint || ''}</FinePrint>
                        </Box>
                    </Root>
                </Box>
            )}
        </Box>
    );
}

const Root = styled('div')(({ theme }) => ({
    display: 'flex',
    paddingTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        width: theme.breakpoints.values.sm,
        marginRight: 'auto',
        marginLeft: 'auto',
    },
    [theme.breakpoints.up('md')]: {
        width: theme.breakpoints.values.md,
        marginRight: 'auto',
        marginLeft: 'auto',
    },
}));

const Container = styled('div')(({ theme }) => ({
    display: 'flex',
    height: 360,
    justifyContent: 'center',
    alignItems: 'center',
}));

const Header = styled(Paper)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderRadius: 0,
}));

const Stepper = styled(_Stepper)(({ theme }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
}));

const StepLabel = styled(_StepLabel)(({ theme }) => ({
    '& .MuiStepLabel-label': {
        margin: 0,
    },
}));

const FinePrint = styled(Typography)(({ theme }) => ({
    whiteSpace: 'pre-wrap',
}));
FinePrint.defaultProps = { variant: 'caption' };
